import { useEffect } from 'react';
import { breakpoints, Button, Card, Heading, Inline, PageBlock, Stack, Text } from '@etg/wings';
import mobileBackground from '@eti/assets/images/panel/content/error_800.webp';
import tabletBackground from '@eti/assets/images/panel/content/error_1200.webp';
import desktopBackground from '@eti/assets/images/panel/content/error_1600.webp';
import {
  useProperty,
  useSearch,
  useSiteContext,
  useSnowplowTracking,
  useTranslation,
} from '@eti/providers';
import { css } from '@eti/styles';
import { analytics } from '@eti/utils';
import { getResponsivePageViewEvent } from '../../../common/data-layer/dataLayer';
import { useGA4PageViewEvent } from '../../../common/data-layer/ecommerce/custom-hooks/useGA4PageViewEvent';

const blockStyles = css`
  align-content: center;
  background-image: url('${mobileBackground}');
  background-size: cover;
  height: 100%;

  @media (min-width: ${breakpoints._560}) {
    background-image: url('${tabletBackground}');
  }

  @media (min-width: ${breakpoints._1024}) {
    background-image: url('${desktopBackground}');
  }
`;

const cardStyles = css`
  @media (min-width: ${breakpoints._768}) {
    max-width: 39.375rem;
  }
`;

interface ErrorPageProps {
  buttonText?: string;
  descriptionText?: string;
  redirectUrl?: string;
  titleText?: string;
}

const ErrorPage = ({ buttonText, descriptionText, redirectUrl, titleText }: ErrorPageProps) => {
  const { agencyAccount } = useSiteContext();
  const { p } = useProperty();
  const { t } = useTranslation();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const { currentSearchData } = useSearch();
  const { travelers, tripType, routes } = { ...currentSearchData };

  useGA4PageViewEvent({
    responsive_url: '/rf/error',
    page_type: 'Error',
    page_type_detailed: 'Error',
    no_of_adults: travelers?.adults,
    no_of_children: travelers?.childAges?.filter((childAge) => childAge >= 2).length,
    no_of_infants: travelers?.childAges?.filter((childAge) => childAge < 2).length,
    origin_country_code: routes?.[0]?.originCountryCode,
    destination_country_code: routes?.[routes.length - 1]?.originCountryCode,
    pax:
      travelers?.adults || travelers?.childAges?.length
        ? Number(travelers?.adults) + Number(travelers?.childAges?.length)
        : undefined,
    trip_type: tripType,
  });

  useEffect(() => {
    if (!p('IbeClient.GA4.Enabled')) {
      analytics.pushToDataLayer(getResponsivePageViewEvent('/rf/error', 'Error', 'Error'));
    }
  }, [p]);

  useEffect(() => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({ category: 'Error', action: 'Seen', label: 'Page' });
    }
  }, [isSnowplowTrackingEnabled, sendTrackEvent]);

  const errorPageHomeUrl = p('IBEClient.ErrorMessageLink.Url');

  return (
    <PageBlock className={blockStyles} withPaddingY>
      <Inline align="center">
        <Card
          className={cardStyles}
          padding={{ [breakpoints._0]: 16, [breakpoints._560]: 32, [breakpoints._1024]: 40 }}
        >
          <Stack align="center" spacing={24}>
            <Stack align="center" spacing={8}>
              <Heading level={1}>{titleText ? titleText : t('StatusPage.Error.Title')}</Heading>
              <Text asHtml>
                {descriptionText ? descriptionText : t('StatusPage.Error.Message')}
              </Text>
            </Stack>
            {errorPageHomeUrl && !agencyAccount && (
              <Inline collapseBelow={breakpoints._1024} spacing={16}>
                <Button as="a" href={redirectUrl ? redirectUrl : errorPageHomeUrl}>
                  {buttonText ? buttonText : t('StatusPage.LinkLabel.Home')}
                </Button>
              </Inline>
            )}
          </Stack>
        </Card>
      </Inline>
    </PageBlock>
  );
};

export default ErrorPage;
